<template>
  <div class="show_all_content_wrapper">
    <!-- Start:: Main Section -->
    <main>
      <!--  =========== Start:: Filter Form =========== -->
      <div
        class="filter_content_wrapper"
        :class="{ active: filterFormIsActive }"
      >
        <button
          class="filter_toggler"
          @click="filterFormIsActive = !filterFormIsActive"
        >
          <i class="fal fa-times"></i>
        </button>
        <div class="filter_title_wrapper">
          <h5>{{ $t("TITLES.searchBy") }}</h5>
        </div>
        <div class="filter_form_wrapper">
          <form @submit.prevent="submitFilterForm">
            <div class="row justify-content-center align-items-center w-100">
              <!-- Start:: Product Name Input -->
              <base-input
                col="4"
                type="text"
                :placeholder="$t('PLACEHOLDERS.productName')"
                v-model.trim="filterOptions.product_name"
              />
              <!-- End:: Product Name Input -->

              <!-- Start:: Category Input -->
              <base-select-input
                col="4"
                :optionsList="categoriesList"
                :placeholder="$t('PLACEHOLDERS.category')"
                v-model="filterOptions.main_categoryid"
              />
              <!-- End:: Category Input -->

              <!-- Start:: Status Input -->
              <base-select-input
                col="4"
                :optionsList="statusOptions"
                :placeholder="$t('PLACEHOLDERS.offerStatus')"
                v-model="filterOptions.status"
              />
              <!-- End:: Status Input -->
            </div>

            <div class="btns_wrapper">
              <button class="submit_btn" :disabled="isWaitingRequest">
                <i class="fal fa-search"></i>
              </button>
              <button
                class="reset_btn"
                type="button"
                :disabled="isWaitingRequest"
                @click="resetFilter"
              >
                <i class="fal fa-redo"></i>
              </button>
            </div>
          </form>
        </div>
      </div>
      <!--  =========== End:: Filter Form =========== -->

      <!--  =========== Start:: Table Title =========== -->
      <div class="table_title_wrapper">
        <div class="title_text_wrapper">
          <h5>{{ $t("TITLES.offersManagement") }}</h5>
          <button
            v-if="!filterFormIsActive"
            class="filter_toggler"
            @click.stop="filterFormIsActive = !filterFormIsActive"
          >
            <i class="fal fa-search"></i>
          </button>
        </div>

        <div class="title_route_wrapper">
          <router-link to="/offers/create">
            {{ $t("BUTTONS.addOffer") }}
          </router-link>
        </div>
      </div>
      <!--  =========== End:: Table Title =========== -->

      <!--  =========== Start:: Data Table =========== -->
      <v-data-table
        class="thumb"
        :loading="loading"
        :loading-text="$t('TABLES.loadingData')"
        :search="searchValue"
        :headers="tableHeaders"
        :items="tableRows"
        item-class="ltr"
        :items-per-page="paginations.items_per_page"
        hide-default-footer
      >
        <!-- Start:: No Data State -->
        <template v-slot:no-data>
          {{ $t("TABLES.noData") }}
        </template>
        <!-- End:: No Data State -->

        <!-- Start:: Serial Number -->
        <template v-slot:[`item.id`]="{ item, index }">
          <p>
            {{
              (paginations.current_page - 1) * paginations.items_per_page +
              index +
              1
            }}
          </p>
        </template>
        <!-- End:: Serial Number -->

        <!-- Start:: Product Image -->
        <template v-slot:[`item.product.main_image`]="{ item }">
          <div class="table_image_wrapper">
            <button
              class="my-1"
              @click="showImageModal(item.product?.main_image)"
              v-if="item.product?.main_image"
            >
              <img
                class="rounded"
                :src="item.product.main_image"
                :alt="item.product.name"
                width="60"
                height="60"
              />
            </button>
            <h6 v-else>
              -
            </h6>
          </div>
        </template>
        <!-- End:: Product Image -->

        <!-- Start:: Product Name -->
        <template v-slot:[`item.product.name`]="{ item }">
          <span
            v-if="!item.product || !item.product.name"
          >
            -
          </span>
          <span
            v-else
          >
            {{ item.product.name }}
          </span>
        </template>
        <!-- End:: Product Name -->

        <!-- Start:: Category -->
        <template v-slot:[`item.product.category`]="{ item }">
          <p
            v-if="!item.product?.category || !item.product.category.name"
          >
            -
          </p>
          <p v-else>{{ item.product.category.name }}</p>
        </template>
        <!-- End:: Category -->

        <!-- Start:: Created At -->
        <template v-slot:[`item.created_at`]="{ item }">
          <p v-if="!item.created_at">
            -
          </p>
          <p v-else>{{ item.created_at }}</p>
        </template>
        <!-- End:: Created At -->

        <!-- Start:: Status -->
        <template v-slot:[`item.status_label`]="{ item }">
          <div class="status_wrapper">
            <div class="activation" dir="ltr" style="z-index: 1">
              <v-switch
                class="mt-2"
                color="success"
                :input-value="item.is_active"
                :disabled="item.status == 'expired' || loading"
                @change="toggleStatus(item, $event)"
                hide-details
              ></v-switch>
            </div>
          </div>
        </template>
        <!-- End:: Status -->

        <!-- Start:: Status -->
        <template v-slot:[`item.status`]="{ item }">
          <div class="status_wrapper">
            <div class="status_text_wrapper mb-2">
              <v-chip
                :color="getStatusColor(item.status)"
                text-color="white"
                small
                class="status_badge"
              >
                {{ getStatusText(item.status) }}
              </v-chip>
            </div>
          </div>
        </template>
        <!-- End:: Status -->

        <!-- Start:: Actions -->
        <template v-slot:[`item.actions`]="{ item }">
          <div class="actions">
            <a-tooltip placement="bottom">
              <template slot="title">
                <span>{{ $t("BUTTONS.show") }}</span>
              </template>
              <button class="btn_show" @click="showItem(item)">
                <i class="fal fa-eye"></i>
              </button>
            </a-tooltip>

            <a-tooltip placement="bottom">
              <template slot="title">
                <span>{{ $t("BUTTONS.edit") }}</span>
              </template>
              <button class="btn_edit" @click="editItem(item)">
                <i class="fal fa-edit"></i>
              </button>
            </a-tooltip>

            <a-tooltip placement="bottom">
              <template slot="title">
                <span>{{ $t("BUTTONS.delete") }}</span>
              </template>
              <button class="btn_delete" @click="selectDeleteItem(item)">
                <i class="fal fa-trash-alt"></i>
              </button>
            </a-tooltip>

            <!-- Activate Button (only for inactive offers) -->
            <!-- <a-tooltip
              placement="bottom"
              v-if="!item.is_active && item.status !== 'expired'"
            >
              <template slot="title">
                <span>{{ $t("BUTTONS.activate") }}</span>
              </template>
              <button
                class="btn_activate"
                @click="selectActivateItem(item)"
              >
                <i class="fad fa-check-circle"></i>
              </button>
            </a-tooltip> -->

            <!-- Deactivate Button (only for active offers) -->
            <!-- <a-tooltip placement="bottom" v-if="item.is_active">
              <template slot="title">
                <span>{{ $t("BUTTONS.deactivate") }}</span>
              </template>
              <button
                class="btn_deactivate"
                @click="selectDeactivateItem(item)"
              >
                <i class="fad fa-times-circle"></i>
              </button>
            </a-tooltip> -->
          </div>
        </template>
        <!-- End:: Actions -->

        <!-- ======================== Start:: Dialogs ======================== -->
        <template v-slot:top>
          <!-- Start:: Image Modal -->
          <image-modal
            v-if="dialogImage"
            :modalIsOpen="dialogImage"
            :modalImage="selectedItemImage"
            @toggleModal="dialogImage = !dialogImage"
          />
          <!-- End:: Image Modal -->

          <!-- Start:: Delete Modal -->
          <v-dialog v-model="dialogDelete">
            <v-card>
              <v-card-title class="text-h5 justify-center" v-if="itemToDelete">
                {{ $t("TITLES.deleteOfferConfirm") }}
              </v-card-title>
              <v-card-actions>
                <v-btn class="modal_confirm_btn" @click="confirmDeleteItem">{{
                  $t("BUTTONS.ok")
                }}</v-btn>

                <v-btn class="modal_cancel_btn" @click="dialogDelete = false">{{
                  $t("BUTTONS.cancel")
                }}</v-btn>
                <v-spacer></v-spacer>
              </v-card-actions>
            </v-card>
          </v-dialog>
          <!-- End:: Delete Modal -->

          <!-- Start:: Activate Modal -->
          <v-dialog v-model="dialogActivate">
            <v-card>
              <v-card-title class="text-h5 justify-center" v-if="itemToActivate">
                {{ $t("TITLES.activateOfferConfirm") }}
              </v-card-title>
              <v-card-actions>
                <v-btn
                  class="modal_confirm_btn"
                  @click="confirmActivateItem"
                >
                  {{ $t("BUTTONS.ok") }}
                </v-btn>

                <v-btn
                  class="modal_cancel_btn"
                  @click="dialogActivate = false"
                >
                  {{ $t("BUTTONS.cancel") }}
                </v-btn>
                <v-spacer></v-spacer>
              </v-card-actions>
            </v-card>
          </v-dialog>
          <!-- End:: Activate Modal -->

          <!-- Start:: Deactivate Modal -->
          <v-dialog v-model="dialogDeactivate">
            <v-card>
              <v-card-title
                class="text-h5 justify-center"
                v-if="itemToDeactivate"
              >
                {{ $t("TITLES.deactivateOfferConfirm") }}
              </v-card-title>
              <v-card-actions>
                <v-btn
                  class="modal_confirm_btn"
                  @click="confirmDeactivateItem"
                >
                  {{ $t("BUTTONS.ok") }}
                </v-btn>

                <v-btn
                  class="modal_cancel_btn"
                  @click="dialogDeactivate = false"
                >
                  {{ $t("BUTTONS.cancel") }}
                </v-btn>
                <v-spacer></v-spacer>
              </v-card-actions>
            </v-card>
          </v-dialog>
          <!-- End:: Deactivate Modal -->
        </template>
        <!-- ======================== End:: Dialogs ======================== -->
      </v-data-table>
      <!--  =========== End:: Data Table =========== -->
    </main>
    <!-- End:: Main Section -->

    <!-- Start:: Pagination -->
    <template>
      <div class="pagination_container text-center mt-3 mb-0">
        <v-pagination
          class="py-0"
          square
          v-model="paginations.current_page"
          :length="paginations.last_page"
          :total-visible="6"
          @input="updateRouterQueryParam($event)"
          :prev-icon="
            getAppLocale == 'ar' ? 'fal fa-angle-right' : 'fal fa-angle-left'
          "
          :next-icon="
            getAppLocale == 'ar' ? 'fal fa-angle-left' : 'fal fa-angle-right'
          "
        />
      </div>
    </template>
    <!-- End:: Pagination -->
  </div>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "AllOffers",

  computed: {
    ...mapGetters({
      getAppLocale: "AppLangModule/getAppLocale",
    }),

    statusOptions() {
      return [
        {
          id: 1,
          name: this.$t("STATUS.onGoing"),
          value: "active",
        },
        {
          id: 2,
          name: this.$t("STATUS.expired"),
          value: "expired",
        },
        {
          id: 3,
          name: this.$t("STATUS.upcoming"),
          value: "upcoming",
        },
      ];
    },
  },

  data() {
    return {
      // Start:: Loading Data
      loading: false,
      isWaitingRequest: false,
      // End:: Loading Data

      // Start:: Filter Data
      filterFormIsActive: false,
      filterOptions: {
        status: null,
        product_name: null,
        main_categoryid: null,
      },
      categoriesList: [],
      // End:: Filter Data

      // Start:: Table Data
      searchValue: "",
      tableHeaders: [
        {
          text: this.$t("TABLES.Admins.serialNumber"),
          value: "id",
          align: "center",
          width: "80",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.productImage"),
          value: "product.main_image",
          align: "center",
          sortable: false,
          width: "120",
        },
        {
          text: this.$t("PLACEHOLDERS.productName"),
          value: "product.name",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.category"),
          value: "product.category",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.created_at"),
          value: "created_at",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.offerStatus"),
          value: "status",
          align: "center",
          sortable: false,
          width: "150",
        },
        {
          text: this.$t("PLACEHOLDERS.status"),
          value: "status_label",
          align: "center",
          sortable: false,
          width: "150",
        },
        {
          text: this.$t("TABLES.Admins.actions"),
          value: "actions",
          sortable: false,
          align: "center",
        },
      ],
      tableRows: [],
      // End:: Table Data

      // Start:: Dialogs Control Data
      dialogImage: false,
      selectedItemImage: null,
      dialogDelete: false,
      itemToDelete: null,
      dialogActivate: false,
      itemToActivate: null,
      dialogDeactivate: false,
      itemToDeactivate: null,
      // End:: Dialogs Control Data

      // Start:: Pagination Data
      paginations: {
        current_page: 1,
        last_page: 1,
        items_per_page: 15,
      },
      // End:: Pagination Data
    };
  },

  watch: {
    // Start:: Page Query Param Watcher To Get Page Data Based On It's Change
    ["$route.query.page"]() {
      this.paginations.current_page = +this.$route.query.page;
      this.setTableRows();
    },
    // End:: Page Query Param Watcher To Get Page Data Based On It's Change
  },

  methods: {
    // Start:: Get Status Color
    getStatusColor(status) {
      if (status === "active" || status === "ساري") {
        return "success";
      } else if (status === "expired" || status === "منتهي") {
        return "error";
      } else if (status === "upcoming" || status === "مستقبلي") {
        return "info";
      }
      return "grey";
    },
    // End:: Get Status Color

    // Start:: Get Status Text
    getStatusText(status) {
      if (status === "active") {
        return this.$t("STATUS.onGoing");
      } else if (status === "expired") {
        return this.$t("STATUS.expired");
      } else if (status === "upcoming") {
        return this.$t("STATUS.upcoming");
      }
      return status || "-";
    },
    // End:: Get Status Text


    // Start:: Handel Filter
    async submitFilterForm() {
      if (this.$route.query.page !== "1") {
        await this.$router.push({ path: "/offers/all", query: { page: 1 } });
      }
      this.setTableRows();
    },
    async resetFilter() {
      this.filterOptions.status = null;
      this.filterOptions.product_name = null;
      this.filterOptions.main_categoryid = null;
      if (this.$route.query.page !== "1") {
        await this.$router.push({ path: "/offers/all", query: { page: 1 } });
      }
      this.setTableRows();
    },
    // End:: Handel Filter

    // Start:: Set Table Rows
    updateRouterQueryParam(pagenationValue) {
      this.$router.push({
        query: {
          ...this.$route.query,
          page: pagenationValue,
        },
      });

      // Scroll To Screen's Top After Get Offers
      document.body.scrollTop = 0; // For Safari
      document.documentElement.scrollTop = 0; // For Chrome, Firefox, IE and Opera
    },
    async setTableRows() {
      this.loading = true;
      try {
        let res = await this.$axios({
          method: "GET",
          url: "offers?per_page=10",
          params: {
            page: this.paginations.current_page,
            "filter[status]": this.filterOptions.status?.value,
            "filter[product_name]": this.filterOptions.product_name || undefined,
            "filter[main_category_id]": this.filterOptions.main_categoryid?.value || undefined,
          },
        });
        this.loading = false;
        this.tableRows = res.data.data;
        this.paginations.last_page = res.data.pagination?.last_page || 1;
        this.paginations.items_per_page =
          res.data.pagination?.per_page || 15;
      } catch (error) {
        this.loading = false;
        console.log(error.response?.data?.message);
        this.$message.error(
          error.response?.data?.message || this.$t("MESSAGES.errorOccurred")
        );
      }
    },
    // End:: Set Table Rows

    // Start:: Control Modals
    showImageModal(image) {
      this.dialogImage = true;
      this.selectedItemImage = image;
    },
    // End:: Control Modals

    // ==================== Start:: Crud ====================
    // ===== Start:: Show
    showItem(item) {
      this.$router.push({ path: `/offers/show/${item.id}` });
    },
    // ===== End:: Show

    // ===== Start:: Edit
    editItem(item) {
      this.$router.push({ path: `/offers/edit/${item.id}` });
    },
    // ===== End:: Edit

    // ===== Start:: Delete
    selectDeleteItem(item) {
      this.dialogDelete = true;
      this.itemToDelete = item;
    },
    async confirmDeleteItem() {
      try {
        await this.$axios({
          method: "DELETE",
          url: `offers/${this.itemToDelete.id}`,
        });
        this.dialogDelete = false;
        this.setTableRows();
        this.$message.success(this.$t("MESSAGES.deletedSuccessfully"));
      } catch (error) {
        this.dialogDelete = false;
        this.$message.error(
          error.response?.data?.message || this.$t("MESSAGES.errorOccurred")
        );
      }
    },
    // ===== End:: Delete

    // ===== Start:: Activate
    selectActivateItem(item) {
      this.dialogActivate = true;
      this.itemToActivate = item;
    },
    async confirmActivateItem() {
      try {
        await this.$axios({
          method: "PATCH",
          url: `offers/${this.itemToActivate.id}/activate`,
        });
        this.dialogActivate = false;
        this.setTableRows();
        this.$message.success(this.$t("MESSAGES.activatedSuccessfully"));
      } catch (error) {
        this.dialogActivate = false;
        this.$message.error(
          error.response?.data?.message || this.$t("MESSAGES.errorOccurred")
        );
      }
    },
    // ===== End:: Activate

    // ===== Start:: Deactivate
    selectDeactivateItem(item) {
      this.dialogDeactivate = true;
      this.itemToDeactivate = item;
    },
    async confirmDeactivateItem() {
      try {
        await this.$axios({
          method: "PATCH",
          url: `offers/${this.itemToDeactivate.id}/deactivate`,
        });
        this.dialogDeactivate = false;
        this.setTableRows();
        this.$message.success(this.$t("MESSAGES.deactivatedSuccessfully"));
      } catch (error) {
        this.dialogDeactivate = false;
        this.$message.error(
          error.response?.data?.message || this.$t("MESSAGES.errorOccurred")
        );
      }
    },
    // ===== End:: Deactivate

    // ===== Start:: Toggle Status
    async toggleStatus(item, newValue) {
      try {
        if (newValue) {
          // Activate
          await this.$axios({
            method: "PATCH",
            url: `offers/${item.id}/activate`,
          });
          this.$message.success(this.$t("MESSAGES.activatedSuccessfully"));
        } else {
          // Deactivate
          await this.$axios({
            method: "PATCH",
            url: `offers/${item.id}/deactivate`,
          });
          this.$message.success(this.$t("MESSAGES.deactivatedSuccessfully"));
        }
        // Refresh table data to reflect the change
        this.setTableRows();
      } catch (error) {
        // Refresh table data to revert the UI state
        this.setTableRows();
        this.$message.error(
          error.response?.data?.message || this.$t("MESSAGES.errorOccurred")
        );
      }
    },
    // ===== End:: Toggle Status
    // ==================== End:: Crud ====================

    // Start:: Get Categories
    async getCategories() {
      try {
        let res = await this.$axios({
          method: "GET",
          url: "product-categories?paginate=false&filter[is_active]=true",
        });
        this.categoriesList = [
          {
            id: null,
            name: this.$t("STATUS.all"),
            value: null,
          },
          ...(res.data.data || []).map((category) => ({
            id: category.id,
            name: category.name,
            value: category.id,
          })),
        ];
      } catch (error) {
        console.log(error.response?.data?.message);
        this.$message.error(
          error.response?.data?.message || this.$t("MESSAGES.errorOccurred")
        );
      }
    },
    // End:: Get Categories
  },

  created() {
    // Start:: Fire Methods
    window.addEventListener("click", () => {
      this.filterFormIsActive = false;
    });
    if (this.$route.query.page) {
      this.paginations.current_page = +this.$route.query.page;
    }
    this.getCategories();
    this.setTableRows();
    // End:: Fire Methods
  },
};
</script>